import React from 'react';
import CameraFrame from '../components/ui/CameraFrame';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiActivity, FiFolderPlus, FiVideo, FiTrendingUp, FiClock, FiCheckSquare } = FiIcons;

const Dashboard = ({ onNavigate }) => {
  const stats = [
    { label: 'Active Sessions', value: '03', icon: FiActivity, color: 'text-pink-500' },
    { label: 'Pending Edits', value: '18', icon: FiClock, color: 'text-yellow-500' },
    { label: 'Completed Deliveries', value: '142', icon: FiCheckSquare, color: 'text-green-500' },
    { label: 'Growth Rate', value: '+12%', icon: FiTrendingUp, color: 'text-blue-400' },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-10 animate-in fade-in duration-500">
      <header>
        <h2 className="font-mono text-3xl text-white tracking-widest uppercase flex items-center gap-3">
          <SafeIcon icon={FiActivity} className="text-pink-500" />
          Command Overview
        </h2>
        <p className="font-mono text-sm text-[#666] mt-2">System health: Optimal. All nodes operational.</p>
      </header>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, i) => (
          <CameraFrame key={i} className="p-5 bg-[#111]">
            <div className="flex justify-between items-start mb-4">
              <SafeIcon icon={stat.icon} className={`w-5 h-5 ${stat.color}`} />
              <span className="font-mono text-[9px] text-[#444] tracking-widest uppercase">RT_UPDATE</span>
            </div>
            <p className="text-3xl font-black text-white mb-1">{stat.value}</p>
            <p className="font-mono text-[10px] text-[#666] uppercase tracking-widest">{stat.label}</p>
          </CameraFrame>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Main Project Card */}
        <div className="md:col-span-2 cursor-pointer group" onClick={() => onNavigate('project')}>
          <CameraFrame className="h-full p-8 bg-[#111] hover:bg-[#151515] transition-all border-pink-500/20 group-hover:border-pink-500/50 group-hover:shadow-[0_0_30px_rgba(236,72,153,0.05)]">
            <div className="flex justify-between items-start mb-16">
              <div>
                <span className="font-mono text-[10px] text-pink-500 tracking-widest uppercase px-3 py-1 border border-pink-500/30 bg-pink-500/10 mb-4 inline-block">Priority Production</span>
                <h3 className="text-4xl font-bold text-white tracking-tight">NEON-NIGHTS AW24</h3>
                <p className="font-mono text-sm text-[#888] mt-2 italic">Client: CyberWear Inc. • Sector 4 Industrial</p>
              </div>
              <div className="p-4 bg-pink-500/5 rounded-full border border-pink-500/10 group-hover:bg-pink-500 group-hover:text-white transition-all">
                <SafeIcon icon={FiVideo} className="w-8 h-8" />
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div className="space-y-3 flex-1">
                <div className="flex justify-between font-mono text-[10px] text-[#666]">
                  <span>PRODUCTION PROGRESS</span>
                  <span>66%</span>
                </div>
                <div className="w-full h-1.5 bg-[#222] rounded-full overflow-hidden">
                  <div className="h-full bg-pink-500 w-[66%] shadow-[0_0_10px_rgba(236,72,153,0.5)]"></div>
                </div>
              </div>
              <div className="flex items-center gap-2 font-mono text-xs text-pink-500 font-bold uppercase tracking-widest group-hover:gap-4 transition-all">
                Enter Command <span className="text-xl">→</span>
              </div>
            </div>
          </CameraFrame>
        </div>

        {/* Action Column */}
        <div className="space-y-6">
          <button 
            onClick={() => onNavigate('demo')}
            className="w-full h-40 border border-pink-500/30 bg-pink-500/5 hover:bg-pink-500/10 transition-all flex flex-col items-center justify-center gap-4 group"
          >
            <div className="w-12 h-12 rounded-full border border-pink-500 flex items-center justify-center group-hover:scale-110 transition-transform">
              <SafeIcon icon={FiVideo} className="w-5 h-5 text-pink-500" />
            </div>
            <span className="font-mono text-xs text-white uppercase tracking-[0.2em]">Explore Demo Flow</span>
          </button>

          <button className="w-full h-40 border border-[#222] border-dashed hover:border-white/20 hover:bg-[#111] transition-all flex flex-col items-center justify-center gap-4 group">
            <div className="w-12 h-12 rounded-full bg-[#1a1a1a] flex items-center justify-center group-hover:scale-110 group-hover:bg-white/5 transition-transform">
              <SafeIcon icon={FiFolderPlus} className="w-5 h-5 text-[#444] group-hover:text-[#888]" />
            </div>
            <span className="font-mono text-xs text-[#555] group-hover:text-white uppercase tracking-[0.2em]">Initialize Shoot</span>
          </button>
        </div>
      </div>
      
      {/* Activity Log */}
      <div className="pt-8">
        <h4 className="font-mono text-[10px] text-[#444] uppercase tracking-[0.3em] mb-4 border-b border-[#222] pb-2">Global System Log</h4>
        <div className="space-y-3 font-mono text-xs text-[#666]">
          <div className="flex gap-4 items-center group">
            <span className="text-[#333] shrink-0">10:42:14</span> 
            <span className="text-green-500 border border-green-500/20 px-1 text-[9px]">SYS</span> 
            <span className="group-hover:text-[#aaa] transition-colors">Asset sync completed for PRJ-8842. Redundancy verified.</span>
          </div>
          <div className="flex gap-4 items-center group">
            <span className="text-[#333] shrink-0">09:15:02</span> 
            <span className="text-blue-500 border border-blue-500/20 px-1 text-[9px]">USR</span> 
            <span className="group-hover:text-[#aaa] transition-colors">Alex Chen uploaded 5 new references to Moodboard.</span>
          </div>
          <div className="flex gap-4 items-center group">
            <span className="text-[#333] shrink-0">08:44:59</span> 
            <span className="text-yellow-500 border border-yellow-500/20 px-1 text-[9px]">WRN</span> 
            <span className="group-hover:text-[#aaa] transition-colors">Logistics update: Heavy Smoke Machine rental pending confirmation.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;